import type { BillingPlanDto } from "@/modules/subscriptions/dtos/billing-plan.dto"
import type { OrganizationFormData } from "@/modules/organizations/entities/Organization"
import type { CouponValidationDto } from "@/modules/coupon/dtos/coupon.dto"
import { Button } from "@/modules/shared/ui/components/button"
import CouponInput from "@/modules/coupon/ui/components/CouponInput"
import { useState, useEffect } from "react"

interface CheckoutSummaryProps {
  companyForm: OrganizationFormData
  selectedPlan: BillingPlanDto
  isYearly: boolean
  onBack: () => void
  onComplete: () => void
  isProcessing?: boolean
  appliedCoupon?: CouponValidationDto | null
  onCouponChange?: (coupon: CouponValidationDto | null) => void
  onCouponValidate?: (code: string) => Promise<CouponValidationDto>
  submitLabel?: string
  errorMessage?: string
  processingMessage?: string
}

export default function CheckoutSummary({
  companyForm,
  selectedPlan,
  isYearly,
  onBack,
  onComplete,
  isProcessing = false,
  appliedCoupon,
  onCouponChange,
  onCouponValidate,
  submitLabel,
  processingMessage = "Processing your request",
  errorMessage,
}: CheckoutSummaryProps) {
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    if (errorMessage) setLocked(false)
  }, [errorMessage])

  const handleComplete = () => {
    setLocked(true)
    onComplete()
  }

  const showOverlay = locked || isProcessing

  const discount = selectedPlan.discount ?? 0.10
  const computedAnnualPrice = 12 * selectedPlan.monthlyPrice * (1 - discount)
  const computedAnnualPriceIncVat = computedAnnualPrice * 1.15

  const basePrice = isYearly ? computedAnnualPrice : selectedPlan.monthlyPrice
  const selectedPrice = isYearly ? computedAnnualPriceIncVat : selectedPlan.monthlyPriceIncVat
  const selectedUnit = isYearly ? "/yr" : "/mo"
  const billingLabel = isYearly ? "Billed annually" : "Billed monthly"
  const vatAmount = selectedPrice - basePrice

  const annualSavings = 12 * selectedPlan.monthlyPrice - computedAnnualPrice

  const couponApplied = appliedCoupon?.isValid ?? false
  const effectiveTrialDays = couponApplied
    ? appliedCoupon!.trialDurationDays
    : selectedPlan.trialDays
  const hasTrial = effectiveTrialDays > 0

  return (
    <div className="relative max-w-4xl mx-auto">
      {showOverlay && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 rounded-lg bg-background/80 backdrop-blur-md">
          <div className="relative h-14 w-14">
            <div className="absolute inset-0 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin [animation-duration:0.9s]" />
            <div className="absolute inset-2 rounded-full border-[3px] border-primary/20 border-b-primary animate-spin [animation-duration:0.6s] [animation-direction:reverse]" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-primary animate-ping" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-semibold text-foreground">{processingMessage}</p>
            <p className="text-xs text-muted-foreground">Please don't close this window</p>
          </div>
        </div>
      )}
      <p className="text-center mb-8 text-sm text-muted-foreground">
        Review your order details before completing registration
      </p>

      <div className="grid md:grid-cols-5 gap-6 items-start">
        {/* Left: Review Details */}
        <div className="md:col-span-3 space-y-4">
          {/* Company Details */}
          <div className="border border-border rounded-lg p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Company Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Company</span>
                <span className="font-medium text-muted-foreground text-right">
                  {companyForm.companyName}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Address</span>
                <span className="font-medium text-muted-foreground text-right">
                  {companyForm.streetAddress},{" "}
                  {companyForm.city}
                  <br />
                  {companyForm.state} {companyForm.zipCode},{" "}
                  {companyForm.country}
                </span>
              </div>
            </div>
          </div>

          {/* Plan Details */}
          <div className="border border-border rounded-lg p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Plan Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Plan</span>
                <span className="font-medium text-muted-foreground text-right">
                  {selectedPlan.name}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Billing</span>
                <span className="font-medium text-muted-foreground text-right">
                  {isYearly ? "Annual" : "Monthly"}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground shrink-0">Max Users</span>
                <span className="font-medium text-muted-foreground text-right">
                  Up to {selectedPlan.maxUsers}
                </span>
              </div>
              {hasTrial && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">Free Trial</span>
                  <span className="font-medium text-muted-foreground text-right">
                    {effectiveTrialDays} days
                    {couponApplied && (
                      <span className="ml-1 text-primary text-xs">
                        (extended by coupon)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {selectedPlan.description && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">Features</span>
                  <span className="font-medium text-muted-foreground text-right max-w-xs">
                    {selectedPlan.description}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Order Summary + Coupon + Actions */}
        <div className="md:col-span-2 space-y-4">
          {/* Order Summary */}
          <div className="border border-border rounded-lg p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Order Summary
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>
                  {selectedPlan.name} ({isYearly ? "Annual" : "Monthly"})
                </span>
                <span>R{basePrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>VAT (15%)</span>
                <span>R{vatAmount.toFixed(2)}</span>
              </div>
              {isYearly && annualSavings > 0 && (
                <div className="flex justify-between text-primary text-xs font-medium">
                  <span>Annual discount</span>
                  <span>
                    -{(discount * 100).toFixed(0)}% (R
                    {annualSavings.toFixed(2)})
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-border mt-4 pt-4">
              {hasTrial ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-muted-foreground">
                      Due today
                    </span>
                    <span className="text-2xl font-bold text-primary">
                      R0.00
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {effectiveTrialDays}-day free trial starts immediately
                  </p>
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-muted-foreground">
                      After trial ({effectiveTrialDays} days)
                    </span>
                    <span className="text-sm font-semibold text-primary">
                      R{selectedPrice.toFixed(2)}{selectedUnit}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-right mt-0.5">
                    {billingLabel}
                  </p>
                </>
              ) : (
                <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-muted-foreground">
                        Total due today
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        R{selectedPrice.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                    {billingLabel} · {selectedUnit}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Coupon Input */}
          {onCouponChange && onCouponValidate && (
            <CouponInput
              onCouponChange={onCouponChange}
              onValidate={onCouponValidate}
              disabled={isProcessing}
            />
          )}

          {/* Action Buttons */}
          <div className="space-y-2 pt-1">
            {errorMessage && (
              <p className="text-sm text-destructive text-center py-1">
                {errorMessage}
              </p>
            )}
            <Button
              onClick={handleComplete}
              disabled={isProcessing}
              className="w-full py-2.5 text-primary-foreground hover:bg-primary"
            >
              {isProcessing
                ? "Processing..."
                : hasTrial
                  ? `Start ${effectiveTrialDays}-Day Free Trial`
                  : (submitLabel ?? "Complete Registration & Pay")}
            </Button>
            <Button
              variant="outline"
              className="w-full py-2.5 text-muted-foreground bg-muted hover:text-foreground"
              onClick={onBack}
              disabled={isProcessing}
            >
              Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
